import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import type { PortalEvaluation } from "@/services/portal"
import { formatShortDate } from "@/lib/portal-dates"

/**
 * Lecture d'une évaluation du portail telle que le serveur l'envoie.
 * Forme : `PortalEvaluation` de `src/api/handlers/learner_portal/`.
 * Les libellés viennent du référentiel.
 * Jamais d'une table recopiée côté écran.
 */

/** Libellé du type d'évaluation, lu dans ref_evaluation_type par le serveur. */
export function portalEvaluationTypeLabel(evaluation: Pick<PortalEvaluation, "evaluation_type_label">): string {
  return evaluation.evaluation_type_label
}

/** « À compléter avant le … » : seulement pour une évaluation qui attend une réponse et porte une échéance. */
export function portalEvaluationDeadline(
  evaluation: Pick<PortalEvaluation, "awaits_response" | "expires_at">,
): O.Option<string> {
  return pipe(
    O.fromNullable(evaluation.expires_at),
    O.filter(() => evaluation.awaits_response),
    O.map((date) => `À compléter avant le ${formatShortDate(date)}`),
  )
}

const SCORE = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 })

/** Note obtenue sur 100, en notation française (« 83,33 / 100 »). */
export function portalEvaluationScore(evaluation: Pick<PortalEvaluation, "overall_score">): O.Option<string> {
  return pipe(
    O.fromNullable(evaluation.overall_score),
    O.map((score) => `Note obtenue : ${SCORE.format(score)} / 100`),
  )
}

/** Taille de page demandée au portail. Borne du serveur (`list_evaluations`). */
export const PORTAL_EVALUATIONS_PER_PAGE = 100

/**
 * Pages lues au plus pour une inscription, soit 2 000 évaluations.
 * La liste n'est pas tronquée à la première page.
 * Le compteur lit toujours `pagination.total`.
 */
export const PORTAL_EVALUATIONS_MAX_PAGES = 20

export interface PortalEvaluationGroups<T> {
  /** Statut non final : en attente de la réponse du stagiaire. */
  pending: T[]
  completed: T[]
  /** Statut final sans réponse (expirée, annulée) : affichée avec son libellé, jamais masquée. */
  closed: T[]
}

/** Répartit les évaluations du stagiaire ; chacune tombe dans un seul groupe. */
export function groupPortalEvaluations<T extends Pick<PortalEvaluation, "awaits_response" | "status">>(
  evaluations: ReadonlyArray<T>,
): PortalEvaluationGroups<T> {
  return {
    pending: evaluations.filter((e) => e.awaits_response),
    completed: evaluations.filter((e) => !e.awaits_response && e.status === "completed"),
    closed: evaluations.filter((e) => !e.awaits_response && e.status !== "completed"),
  }
}

/**
 * Réponse à une question à choix multiples, telle que le serveur l'attend :
 * tableau JSON des libellés cochés (vide = sans réponse).
 */
export function encodeMultipleChoice(labels: ReadonlyArray<string>): string {
  return labels.length === 0 ? "" : JSON.stringify(labels)
}

/** Libellés cochés relus d'une réponse enregistrée ; une valeur illisible vaut aucun choix. */
export function decodeMultipleChoice(answer: string): string[] {
  if (answer.trim().length === 0) return []
  try {
    const parsed: unknown = JSON.parse(answer)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []
  } catch {
    return []
  }
}

/**
 * Lien vers les évaluations du portail : la formation y est présélectionnée
 * (?registration=) quand une seule formation attend des réponses.
 */
export function portalEvaluationsLink(
  registrations: ReadonlyArray<{ id: string; evaluations_pending: number }>,
): string {
  const awaiting = registrations.filter((r) => r.evaluations_pending > 0)
  return awaiting.length === 1
    ? `/portal/evaluations?registration=${encodeURIComponent(awaiting[0].id)}`
    : "/portal/evaluations"
}
